import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { cn } from '@/lib/utils';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import {
    Bell, Send, Users, FileText, Plus, Edit2, Trash2, X,
    RefreshCw, Loader2, ChevronDown, ChevronUp, Check,
    Smartphone, Globe, Search, UserCheck, AlertTriangle,
    Zap, Radio, Eye, Image, Link, CheckSquare,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface PushStats {
    total_users: number;
    total_subscriptions: number;
    unique_users_with_subscriptions: number;
    fcm_subscriptions: number;
    web_subscriptions: number;
    apns_subscriptions: number;
    users_with_push_enabled: number;
    users_with_push_disabled: number;
}

interface PushTemplate {
    id: string;
    category: string;
    notification_type: string;
    title: string;
    message: string;
    is_active: boolean;
    created_at: string;
}

interface SubscriptionDetail {
    subscription_id: string;
    user_id: string;
    display_name: string;
    email: string;
    endpoint: string;
    platform: string;
    created_at: string;
}

interface SendForm {
    title: string;
    message: string;
    waterType: string;
    modalities: string[];
    targetUserId: string | null;
    targetUserName: string | null;
    imageUrl: string;
    clickUrl: string;
    targetPlatform: 'all' | 'app' | 'web';
}

interface UserSearchResult {
    user_id: string;
    display_name: string | null;
    email: string | null;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const NOTIFICATION_CATEGORIES = [
    'Capturas', 'Publicaciones', 'Interacciones Sociales',
    'Batallas', 'Anuncios', 'Compartir', 'Planificar',
];

/** Defaults del cron editorial `pescadillo_best_fishing_day` (imagen + deep link). */
const BEST_FISHING_DAY_IMAGE_URL =
    'https://aipecesprost.blob.core.windows.net/push-images/editorial/best-fishing-day-activity.png';
const BEST_FISHING_DAY_CLICK_URL = '/rincon?tab=planificar';
const BEST_FISHING_DAY_TYPE = 'pescadillo_best_fishing_day';

const FISHING_MODALITIES = [
    'Big game', 'Carpfishing', 'Casting', 'Cebo Vivo', 'Chambel',
    'Corcheo', 'Curricán', 'Eging', 'Feeder', 'Finesse de agua dulce',
    'Pesca de Fondo', 'Jigging', 'Jig Casting', 'Micro-jigging',
    'Pesca a mosca', 'Popping', 'Rockfishing', 'Spinning',
    'Surfcasting', 'Tip Run',
];

const WATER_TYPES = [
    { value: 'all', label: 'Todos' },
    { value: 'dulce', label: 'Agua dulce' },
    { value: 'salada', label: 'Agua salada' },
];

const PLATFORM_TARGETS = [
    { value: 'all' as const, label: 'Todas', icon: <Radio size={12} /> },
    { value: 'app' as const, label: 'App', icon: <Smartphone size={12} /> },
    { value: 'web' as const, label: 'Web', icon: <Globe size={12} /> },
];

/** El token guardado es Firebase (`fcm:`) en Android y en iOS. La RPC antigua etiqueta ese token como android. */
function pushChannelLabel(platform: string): 'App' | 'Web' {
    return platform === 'web' ? 'Web' : 'App';
}

/**
 * App pide el conjunto nativo. Se envía `ios` porque la función ya desplegada
 * agrupa ahí Firebase (`fcm:`) y los restos `apns:`, sin el navegador.
 */
function pushTargetForApi(target: SendForm['targetPlatform']): 'ios' | 'web' | undefined {
    if (target === 'all') return undefined;
    if (target === 'web') return 'web';
    return 'ios';
}

const EMPTY_FORM: SendForm = { title: '', message: '', waterType: 'all', modalities: [], targetUserId: null, targetUserName: null, imageUrl: '', clickUrl: '', targetPlatform: 'all' };

type NotificationsConfirmAction =
    | { kind: 'send-individual'; snapshot: SendForm }
    | { kind: 'send-broadcast'; snapshot: SendForm; userIds: string[] }
    | { kind: 'delete-template'; templateId: string };

const PLATFORM_ICONS: Record<'App' | 'Web', React.ReactNode> = {
    App: <Smartphone size={14} className="text-primary" />,
    Web: <Globe size={14} className="text-primary" />,
};

// ─── Component ──────────────────────────────────────────────────────────────

export function NotificationsPage() {
    const queryClient = useQueryClient();
    const [form, setForm] = useState<SendForm>(EMPTY_FORM);
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState<{ sent: number; failed: number; total: number } | null>(null);
    const [showSegmentation, setShowSegmentation] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [editDialog, setEditDialog] = useState<{ open: boolean; template: PushTemplate | null }>({ open: false, template: null });
    const [templateForm, setTemplateForm] = useState({ category: '', notification_type: '', title: '', message: '', is_active: true });
    const [userSearch, setUserSearch] = useState('');
    const [showUserSearch, setShowUserSearch] = useState(false);
    const [sendMode, setSendMode] = useState<'individual' | 'broadcast'>('individual');
    const [showSubscriptions, setShowSubscriptions] = useState(false);
    const [confirmAction, setConfirmAction] = useState<NotificationsConfirmAction | null>(null);

    // ─── Stats ─────────────────────────────────
    const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
        queryKey: ['admin', 'push-stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<PushStats>('admin_get_push_stats');
            if (error) throw error;
            // admin_get_push_stats returns JSON directly (single value), not array
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                return data as PushStats;
            }
            const normalized = normalizeRpcResponse(data);
            return (normalized[0] || {
                total_users: 0,
                total_subscriptions: 0,
                unique_users_with_subscriptions: 0,
                fcm_subscriptions: 0,
                web_subscriptions: 0,
                apns_subscriptions: 0,
                users_with_push_enabled: 0,
                users_with_push_disabled: 0,
            }) as PushStats;
        },
    });

    // ─── User Search for targeted send ──────────
    const { data: searchResults = [], isLoading: searchLoading } = useQuery({
        queryKey: ['admin', 'search-users-push', userSearch],
        queryFn: async () => {
            if (!userSearch || userSearch.trim().length < 2) return [];
            const { data, error } = await dbClient.rpc<UserSearchResult[]>('admin_search_users', {
                p_search_query: userSearch.trim(),
                p_search_by_email: true,
                p_result_limit: 10,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as UserSearchResult[];
        },
        enabled: userSearch.trim().length >= 2,
        staleTime: 30_000,
    });

    // ─── Subscription Details ───────────────────
    const { data: subscriptions = [], isLoading: subsLoading } = useQuery({
        queryKey: ['admin', 'push-subscriptions-detail'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<SubscriptionDetail[]>('admin_get_push_subscriptions_detail', {
                p_limit: 50,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as SubscriptionDetail[];
        },
        enabled: showSubscriptions,
    });

    // ─── Templates ─────────────────────────────
    const { data: templates = [], isLoading: templatesLoading } = useQuery({
        queryKey: ['admin', 'push-templates'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<PushTemplate[]>('admin_get_push_templates');
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    // ─── Send Push ─────────────────────────────
    const runConfirmedSend = useCallback(
        async (action: Extract<NotificationsConfirmAction, { kind: 'send-individual' | 'send-broadcast' }>) => {
            const snapshot = action.snapshot;
            setSending(true);
            setSendResult(null);

            try {
                if (action.kind === 'send-individual') {
                    if (!snapshot.targetUserId) {
                        setSendResult({ sent: 0, failed: 1, total: 1 });
                        return;
                    }
                    const res = await apiClient.post<any>('/send-push-notification', {
                        user_id: snapshot.targetUserId,
                        title: snapshot.title.trim(),
                        message: snapshot.message.trim(),
                        type: 'announcement',
                        image_url: snapshot.imageUrl.trim() || undefined,
                        click_url: snapshot.clickUrl.trim() || undefined,
                        target_platform: pushTargetForApi(snapshot.targetPlatform),
                    });

                    if (res.error) throw new Error(res.error.message);
                    const sent = res.data?.sent || (res.data?.success ? 1 : 0);
                    const total = res.data?.total || 1;
                    setSendResult({ sent, failed: total - sent, total });
                    setForm(EMPTY_FORM);
                    return;
                }

                const ids = action.userIds;
                let sentCount = 0;
                let failedCount = 0;
                const BATCH_SIZE = 10;

                for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                    const batch = ids.slice(i, i + BATCH_SIZE);

                    const batchResults = await Promise.allSettled(
                        batch.map(async (userId: string) => {
                            const res = await apiClient.post<any>('/send-push-notification', {
                                user_id: userId,
                                title: snapshot.title.trim(),
                                message: snapshot.message.trim(),
                                type: 'announcement',
                                image_url: snapshot.imageUrl.trim() || undefined,
                                click_url: snapshot.clickUrl.trim() || undefined,
                                target_platform: pushTargetForApi(snapshot.targetPlatform),
                            });
                            return res;
                        })
                    );

                    for (const result of batchResults) {
                        if (result.status === 'fulfilled' && !result.value.error) {
                            sentCount += result.value.data?.sent ?? 0;
                        } else {
                            failedCount++;
                        }
                    }

                    if (i + BATCH_SIZE < ids.length) {
                        await new Promise((r) => setTimeout(r, 500));
                    }
                }

                setSendResult({ sent: sentCount, failed: failedCount, total: ids.length });
                setForm(EMPTY_FORM);
            } catch (err: unknown) {
                console.error('Error sending push:', err);
                setSendResult({ sent: 0, failed: 1, total: 1 });
            } finally {
                setSending(false);
            }
        },
        []
    );

    const beginSend = useCallback(async () => {
        if (!form.title.trim() || !form.message.trim()) return;

        if (sendMode === 'individual' && form.targetUserId) {
            setConfirmAction({ kind: 'send-individual', snapshot: { ...form } });
            return;
        }

        setSending(true);
        setSendResult(null);

        try {
            const params: Record<string, unknown> = {};
            if (form.waterType !== 'all') params.p_water_type = form.waterType;
            if (form.modalities.length > 0) params.p_fishing_modes = form.modalities;

            const { data: userIds, error: segError } = await dbClient.rpc<string[]>('admin_get_segmented_user_ids', params);
            if (segError) throw segError;
            const raw = normalizeRpcResponse(userIds);

            if (raw.length === 0) {
                setSendResult({ sent: 0, failed: 0, total: 0 });
                return;
            }

            const userIdList = raw.map((userId: unknown) =>
                typeof userId === 'object' && userId !== null && 'user_id' in userId
                    ? String((userId as { user_id: string }).user_id)
                    : String(userId)
            );

            setConfirmAction({
                kind: 'send-broadcast',
                snapshot: { ...form },
                userIds: userIdList,
            });
        } catch (err: unknown) {
            console.error('Error preparing broadcast:', err);
            setSendResult({ sent: 0, failed: 1, total: 1 });
        } finally {
            setSending(false);
        }
    }, [form, sendMode]);

    // ─── Template Mutations ────────────────────
    const saveTemplateMutation = useMutation({
        mutationFn: async (tmpl: typeof templateForm & { id?: string }) => {
            const rpcName = tmpl.id ? 'admin_update_push_template' : 'admin_create_push_template';
            const params: Record<string, unknown> = {
                p_category: tmpl.category,
                p_notification_type: tmpl.notification_type,
                p_title: tmpl.title,
                p_message: tmpl.message,
                p_is_active: tmpl.is_active,
            };
            if (tmpl.id) params.p_template_id = tmpl.id;

            const { error } = await dbClient.rpc(rpcName, params);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'push-templates'] });
            setEditDialog({ open: false, template: null });
        },
    });

    const deleteTemplateMutation = useMutation({
        mutationFn: async (id: string) => {
            const { error } = await dbClient.rpc('admin_delete_push_template', { p_template_id: id });
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'push-templates'] }),
    });

    const toggleTemplateMutation = useMutation({
        mutationFn: async (t: PushTemplate) => {
            const { error } = await dbClient.rpc('admin_toggle_push_template', {
                p_template_id: t.id,
                p_is_active: !t.is_active,
            });
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'push-templates'] }),
    });

    const handleConfirmDialog = useCallback(() => {
        setConfirmAction((current) => {
            if (!current) return null;
            if (current.kind === 'delete-template') {
                deleteTemplateMutation.mutate(current.templateId);
                return null;
            }
            void runConfirmedSend(current);
            return null;
        });
    }, [runConfirmedSend, deleteTemplateMutation]);

    // ─── Derived ───────────────────────────────
    const filteredTemplates = categoryFilter === 'all'
        ? templates
        : templates.filter(t => t.category === categoryFilter);

    const templatesByCategory = filteredTemplates.reduce((acc, t) => {
        (acc[t.category] ??= []).push(t);
        return acc;
    }, {} as Record<string, PushTemplate[]>);

    const toggleModality = (mod: string) => {
        setForm(prev => ({
            ...prev,
            modalities: prev.modalities.includes(mod)
                ? prev.modalities.filter(m => m !== mod)
                : [...prev.modalities, mod],
        }));
    };

    const openEditDialog = (template: PushTemplate | null) => {
        setTemplateForm(template
            ? { category: template.category, notification_type: template.notification_type, title: template.title, message: template.message, is_active: template.is_active }
            : { category: '', notification_type: '', title: '', message: '', is_active: true }
        );
        setEditDialog({ open: true, template });
    };

    const selectUser = (user: UserSearchResult) => {
        setForm(prev => ({
            ...prev,
            targetUserId: user.user_id,
            targetUserName: user.display_name || user.email || user.user_id,
        }));
        setShowUserSearch(false);
        setUserSearch('');
    };

    const applyTemplateToForm = (t: PushTemplate) => {
        const isBestDay = t.notification_type === BEST_FISHING_DAY_TYPE;
        let title = t.title;
        let message = t.message;
        if (isBestDay) {
            const dayIdx = (new Date().getDay() + 6) % 7;
            const satOff = ((5 - dayIdx + 7) % 7) || 7;
            const sunOff = ((6 - dayIdx + 7) % 7) || 7;
            const useSat = satOff <= sunOff;
            const day = useSat ? 'sábado' : 'domingo';
            const Day = useSat ? 'Sábado' : 'Domingo';
            title = title.replaceAll('{Day}', Day).replaceAll('{day}', day);
            message = message.replaceAll('{Day}', Day).replaceAll('{day}', day);
        }
        setForm((prev) => ({
            ...prev,
            title,
            message,
            imageUrl: isBestDay ? BEST_FISHING_DAY_IMAGE_URL : prev.imageUrl,
            clickUrl: isBestDay ? BEST_FISHING_DAY_CLICK_URL : prev.clickUrl,
        }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const coveragePercent = stats && stats.total_users > 0
        ? ((stats.unique_users_with_subscriptions / stats.total_users) * 100).toFixed(1)
        : '0';

    return (
        <div className="space-y-6">
            <PageHeader
                title="Notificaciones Push"
                description="Envío segmentado, estadísticas de dispositivos y gestión de plantillas"
            />

            {/* ─── Stats Dashboard ──────────────────────────────────────── */}
            {statsError && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm flex items-center gap-2">
                    <AlertTriangle size={16} />
                    <span>
                        Error al cargar estadísticas. La RPC <code className="bg-amber-500/20 px-1 rounded">admin_get_push_stats</code> podría no existir.
                        Ejecuta la migración <code className="bg-amber-500/20 px-1 rounded">20260211003600_admin_push_notification_rpcs.sql</code>
                    </span>
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard
                    title="Dispositivos Registrados"
                    value={statsLoading ? '...' : stats?.total_subscriptions || 0}
                    icon={<Radio size={18} />}
                />
                <MetricCard
                    title="Usuarios con Push"
                    value={statsLoading ? '...' : stats?.unique_users_with_subscriptions || 0}
                    icon={<UserCheck size={18} />}
                />
                <MetricCard
                    title="Usuarios Totales"
                    value={statsLoading ? '...' : stats?.total_users || 0}
                    icon={<Users size={18} />}
                />
                <MetricCard
                    title="Cobertura Push"
                    value={statsLoading ? '...' : `${coveragePercent}%`}
                    icon={<Zap size={18} />}
                />
            </div>

            {stats && stats.total_subscriptions > 0 && (
                <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-card p-3">
                            <Smartphone size={16} className="text-primary" />
                            <div>
                                <p className="text-xs text-muted-foreground">App (Firebase)</p>
                                <p className="text-sm font-semibold text-foreground">
                                    {Number(stats.fcm_subscriptions) + Number(stats.apns_subscriptions)}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-card p-3">
                            <Globe size={16} className="text-primary" />
                            <div>
                                <p className="text-xs text-muted-foreground">Web</p>
                                <p className="text-sm font-semibold text-foreground">{stats.web_subscriptions}</p>
                            </div>
                        </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                        Android e iOS se registran por Firebase y salen juntos en App.
                    </p>
                </div>
            )}

            {/* ─── Subscriptions Detail Toggle ──────────────────────────── */}
            <button
                onClick={() => setShowSubscriptions(!showSubscriptions)}
                className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
                <Eye size={14} />
                {showSubscriptions ? 'Ocultar suscripciones' : 'Ver detalle de suscripciones'}
                {showSubscriptions ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {showSubscriptions && (
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <h4 className="text-xs font-semibold text-foreground">Dispositivos Registrados</h4>
                    {subsLoading ? (
                        <div className="py-6 flex items-center justify-center">
                            <RefreshCw size={16} className="animate-spin text-muted-foreground" />
                        </div>
                    ) : subscriptions.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">No hay suscripciones push registradas</p>
                    ) : (
                        <div className="max-h-72 overflow-y-auto space-y-1">
                            {subscriptions.map((sub) => {
                                const channel = pushChannelLabel(sub.platform);
                                return (
                                    <div key={sub.subscription_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 text-sm">
                                        {PLATFORM_ICONS[channel]}
                                        <span className="font-medium text-foreground truncate">{sub.display_name || 'Sin nombre'}</span>
                                        <span className="text-xs text-muted-foreground truncate">{sub.email}</span>
                                        <span className="text-[10px] text-muted-foreground/60 ml-auto">{channel}</span>
                                        <span className="text-[10px] text-muted-foreground/60">{new Date(sub.created_at).toLocaleDateString()}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ─── Send Section ─────────────────────────────────────────── */}
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Send size={18} className="text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Enviar Notificación</h3>
                </div>

                {/* Send Mode Toggle */}
                <div className="flex gap-2">
                    <button
                        onClick={() => setSendMode('individual')}
                        className={cn(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                            sendMode === 'individual'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <UserCheck size={12} />
                        Usuario Individual
                    </button>
                    <button
                        onClick={() => setSendMode('broadcast')}
                        className={cn(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                            sendMode === 'broadcast'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Radio size={12} />
                        Envío Masivo
                    </button>
                </div>

                {/* Individual User Selector */}
                {sendMode === 'individual' && (
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground">Destinatario *</label>
                        {form.targetUserId ? (
                            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                                <UserCheck size={14} className="text-emerald-400" />
                                <span className="text-sm font-medium text-emerald-400">{form.targetUserName}</span>
                                <button
                                    onClick={() => setForm(p => ({ ...p, targetUserId: null, targetUserName: null }))}
                                    className="ml-auto p-1 rounded hover:bg-emerald-500/20 transition-colors"
                                >
                                    <X size={14} className="text-emerald-400" />
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background">
                                    <Search size={14} className="text-muted-foreground" />
                                    <input
                                        className="flex-1 bg-transparent text-foreground text-sm focus:outline-none"
                                        placeholder="Buscar usuario por nombre o email..."
                                        value={userSearch}
                                        onChange={e => {
                                            setUserSearch(e.target.value);
                                            setShowUserSearch(true);
                                        }}
                                        onFocus={() => setShowUserSearch(true)}
                                        disabled={sending}
                                    />
                                    {searchLoading && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
                                </div>
                                {showUserSearch && searchResults.length > 0 && (
                                    <div className="absolute z-10 top-full left-0 right-0 mt-1 rounded-lg border border-border bg-card shadow-xl max-h-48 overflow-y-auto">
                                        {searchResults.map(u => (
                                            <button
                                                key={u.user_id}
                                                onClick={() => selectUser(u)}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                                            >
                                                <Users size={14} className="text-muted-foreground shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-foreground truncate">{u.display_name || 'Sin nombre'}</p>
                                                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Title & Message */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">Título *</label>
                        <input
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            placeholder="Ej: Nueva funcionalidad disponible"
                            value={form.title}
                            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                            maxLength={100}
                            disabled={sending}
                        />
                        <span className="text-[10px] text-muted-foreground">{form.title.length}/100</span>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">Mensaje *</label>
                        <textarea
                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                            placeholder="Ej: Hemos añadido nuevas funciones..."
                            value={form.message}
                            onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                            maxLength={500}
                            rows={3}
                            disabled={sending}
                        />
                        <span className="text-[10px] text-muted-foreground">{form.message.length}/500</span>
                    </div>
                </div>

                {/* Image URL */}
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <Image size={12} />
                        Imagen (opcional)
                    </label>
                    <input
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="https://ejemplo.com/imagen.jpg"
                        value={form.imageUrl}
                        onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))}
                        disabled={sending}
                        type="url"
                    />
                    {form.imageUrl.trim() && (
                        <div className="relative mt-2 rounded-lg overflow-hidden border border-border/50 max-w-xs">
                            <img
                                src={form.imageUrl.trim()}
                                alt="Preview"
                                className="w-full h-32 object-cover"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                onLoad={(e) => { (e.target as HTMLImageElement).style.display = 'block'; }}
                            />
                        </div>
                    )}
                </div>

                {/* Click URL */}
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <Link size={12} />
                        URL al hacer clic (opcional)
                    </label>
                    <input
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="https://www.aipeces.com/board o ruta interna como /board?catchId=123"
                        value={form.clickUrl}
                        onChange={e => setForm(p => ({ ...p, clickUrl: e.target.value }))}
                        disabled={sending}
                    />
                    <p className="text-[10px] text-muted-foreground">
                        Deja vacío para usar la navegación por defecto. Acepta URLs completas o rutas internas.
                    </p>
                </div>

                {/* Target Platform */}
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Destino</label>
                    <div className="flex gap-2 flex-wrap">
                        {PLATFORM_TARGETS.map(p => (
                            <button
                                key={p.value}
                                onClick={() => setForm(prev => ({ ...prev, targetPlatform: p.value }))}
                                className={cn(
                                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                                    form.targetPlatform === p.value
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted text-muted-foreground hover:text-foreground'
                                )}
                            >
                                {p.icon}
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                        App envía a los móviles con Firebase, Android e iOS. Web envía al navegador.
                    </p>
                </div>

                {/* Segmentation (only for broadcast) */}
                {sendMode === 'broadcast' && (
                    <>
                        <button
                            onClick={() => setShowSegmentation(!showSegmentation)}
                            className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                        >
                            {showSegmentation ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            Segmentación avanzada
                        </button>

                        {showSegmentation && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg bg-muted/30 border border-border/50">
                                {/* Water Type */}
                                <div className="space-y-2">
                                    <label className="text-xs font-medium text-muted-foreground">Tipo de Agua</label>
                                    <div className="flex gap-2">
                                        {WATER_TYPES.map(w => (
                                            <button
                                                key={w.value}
                                                onClick={() => setForm(p => ({ ...p, waterType: w.value }))}
                                                className={cn(
                                                    'px-3 py-1 rounded-md text-xs font-medium transition-all',
                                                    form.waterType === w.value
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'bg-muted text-muted-foreground hover:text-foreground'
                                                )}
                                            >
                                                {w.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {/* Fishing Modalities */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-medium text-muted-foreground">Modalidades de Pesca</label>
                                        <button
                                            onClick={() => setForm(prev => ({
                                                ...prev,
                                                modalities: prev.modalities.length === FISHING_MODALITIES.length ? [] : [...FISHING_MODALITIES],
                                            }))}
                                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium text-primary hover:bg-primary/10 transition-colors"
                                        >
                                            <CheckSquare size={10} />
                                            {form.modalities.length === FISHING_MODALITIES.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
                                        </button>
                                    </div>
                                    <div className="max-h-36 overflow-y-auto space-y-1 rounded-lg border border-border p-2 bg-background">
                                        {FISHING_MODALITIES.map(mod => (
                                            <label key={mod} className="flex items-center gap-2 cursor-pointer py-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={form.modalities.includes(mod)}
                                                    onChange={() => toggleModality(mod)}
                                                    className="rounded border-border w-3.5 h-3.5 accent-primary"
                                                />
                                                <span className="text-xs text-foreground">{mod}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {form.modalities.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {form.modalities.map(m => (
                                                <span key={m} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-medium">
                                                    {m}
                                                    <X size={10} className="cursor-pointer" onClick={() => toggleModality(m)} />
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Send Result */}
                {sendResult && (
                    <div className={cn(
                        'p-3 rounded-lg text-sm',
                        sendResult.failed > 0 ? 'bg-red-500/10 text-red-400' :
                            sendResult.total === 0 ? 'bg-amber-500/10 text-amber-400' :
                                'bg-emerald-500/10 text-emerald-400'
                    )}>
                        {sendResult.total === 0
                            ? 'No hay usuarios que cumplan los criterios de segmentación.'
                            : `${sendResult.sent} enviadas${sendResult.failed > 0 ? `, ${sendResult.failed} fallidas` : ''} de ${sendResult.total} total.`}
                    </div>
                )}

                {/* Send Progress */}
                {sending && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 size={14} className="animate-spin" />
                        Enviando notificaciones...
                    </div>
                )}

                <button
                    onClick={() => void beginSend()}
                    disabled={
                        sending ||
                        !form.title.trim() ||
                        !form.message.trim() ||
                        (sendMode === 'individual' && !form.targetUserId)
                    }
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {sending ? 'Enviando...' : sendMode === 'individual' ? 'Enviar a Usuario' : 'Enviar Masivo'}
                </button>
            </div>

            {/* ─── Templates Section ────────────────────────────────────── */}
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">Alta actividad (Planificar)</span>
                    {' · '}
                    Cron diario 14:30 · tipo <code className="text-[10px]">pescadillo_best_fishing_day</code>
                    {' · '}solo si la actividad prevista de la zona es alta en 2–3 días · deep link Rincón › Planificar · fase{' '}
                    <code className="text-[10px]">pescadillo_best_day_push.phase=activity</code>
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileText size={18} className="text-primary" />
                        <h3 className="text-sm font-semibold text-foreground">Plantillas de Notificación</h3>
                        <span className="text-xs text-muted-foreground">({templates.length})</span>
                    </div>
                    <button
                        onClick={() => openEditDialog(null)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                    >
                        <Plus size={12} />
                        Nueva
                    </button>
                </div>

                {/* Category Filter */}
                <div className="flex gap-1 flex-wrap">
                    <button
                        onClick={() => setCategoryFilter('all')}
                        className={cn(
                            'px-2.5 py-1 rounded-md text-xs font-medium transition-all',
                            categoryFilter === 'all' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        Todas
                    </button>
                    {NOTIFICATION_CATEGORIES.map(c => (
                        <button
                            key={c}
                            onClick={() => setCategoryFilter(c)}
                            className={cn(
                                'px-2.5 py-1 rounded-md text-xs font-medium transition-all',
                                categoryFilter === c ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                {/* Templates List */}
                {templatesLoading ? (
                    <div className="py-12 flex items-center justify-center">
                        <RefreshCw size={18} className="animate-spin text-muted-foreground" />
                    </div>
                ) : Object.keys(templatesByCategory).length === 0 ? (
                    <div className="py-12 text-center text-sm text-muted-foreground">
                        No hay plantillas {categoryFilter !== 'all' ? `en "${categoryFilter}"` : ''}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {Object.entries(templatesByCategory).map(([category, items]) => (
                            <div key={category}>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xs font-semibold text-foreground">{category}</span>
                                    <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded bg-muted">{items.length}</span>
                                </div>
                                <div className="space-y-1">
                                    {items.map(t => (
                                        <div
                                            key={t.id}
                                            className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:border-border transition-colors group"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-mono text-muted-foreground">{t.notification_type}</span>
                                                    <StatusBadge status={t.is_active ? 'active' : 'inactive'} />
                                                </div>
                                                <p className="text-sm font-medium text-foreground mt-0.5 truncate">{t.title}</p>
                                                <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.message}</p>
                                            </div>
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => applyTemplateToForm(t)}
                                                    className="px-2 py-1 rounded-md hover:bg-primary/10 transition-colors text-[10px] font-medium text-primary"
                                                    title="Cargar en el formulario de envío"
                                                >
                                                    Usar
                                                </button>
                                                <button
                                                    onClick={() => toggleTemplateMutation.mutate(t)}
                                                    className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                                    title={t.is_active ? 'Desactivar' : 'Activar'}
                                                >
                                                    <Check size={14} />
                                                </button>
                                                <button
                                                    onClick={() => openEditDialog(t)}
                                                    className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        setConfirmAction({ kind: 'delete-template', templateId: t.id })
                                                    }
                                                    className="p-1.5 rounded-md hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-400"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {confirmAction && (
                <AdminConfirmDialog
                    open
                    variant={confirmAction.kind === 'delete-template' ? 'danger' : 'default'}
                    title={
                        confirmAction.kind === 'delete-template'
                            ? 'Eliminar plantilla'
                            : confirmAction.kind === 'send-individual'
                              ? 'Confirmar notificación'
                              : 'Confirmar envío masivo'
                    }
                    description={
                        confirmAction.kind === 'delete-template' ? (
                            <p>La plantilla se eliminará de forma permanente. Esta acción no se puede deshacer.</p>
                        ) : confirmAction.kind === 'send-individual' ? (
                            <>
                                <p>
                                    Se enviará una notificación push a{' '}
                                    <span className="font-medium text-foreground">
                                        {confirmAction.snapshot.targetUserName || confirmAction.snapshot.targetUserId}
                                    </span>
                                    .
                                </p>
                                <div className="rounded-lg border border-border/50 bg-muted/20 p-3 text-xs space-y-2">
                                    <p>
                                        <span className="text-muted-foreground">Título: </span>
                                        <span className="text-foreground">{confirmAction.snapshot.title}</span>
                                    </p>
                                    <p>
                                        <span className="text-muted-foreground">Mensaje: </span>
                                        <span className="text-foreground whitespace-pre-wrap">{confirmAction.snapshot.message}</span>
                                    </p>
                                </div>
                            </>
                        ) : (
                            <>
                                <p>
                                    Se enviará una notificación push a{' '}
                                    <span className="font-semibold text-foreground">{confirmAction.userIds.length}</span>{' '}
                                    usuarios según la segmentación actual. Esta acción no se puede deshacer.
                                </p>
                                <div className="rounded-lg border border-border/50 bg-muted/20 p-3 text-xs space-y-2">
                                    <p>
                                        <span className="text-muted-foreground">Título: </span>
                                        <span className="text-foreground">{confirmAction.snapshot.title}</span>
                                    </p>
                                    <p>
                                        <span className="text-muted-foreground">Mensaje: </span>
                                        <span className="text-foreground whitespace-pre-wrap">{confirmAction.snapshot.message}</span>
                                    </p>
                                </div>
                            </>
                        )
                    }
                    confirmLabel={confirmAction.kind === 'delete-template' ? 'Eliminar' : 'Enviar'}
                    cancelLabel="Volver"
                    confirmDisabled={deleteTemplateMutation.isPending}
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={handleConfirmDialog}
                />
            )}

            {/* ─── Edit Dialog ──────────────────────────────────────────── */}
            {editDialog.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setEditDialog({ open: false, template: null })}>
                    <div className="w-full max-w-lg mx-4 rounded-xl border border-border bg-card p-6 space-y-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-foreground">
                                {editDialog.template ? 'Editar Plantilla' : 'Nueva Plantilla'}
                            </h3>
                            <button onClick={() => setEditDialog({ open: false, template: null })} className="text-muted-foreground hover:text-foreground">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Categoría *</label>
                                <select
                                    value={templateForm.category}
                                    onChange={e => setTemplateForm(p => ({ ...p, category: e.target.value }))}
                                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                                >
                                    <option value="">Seleccionar...</option>
                                    {NOTIFICATION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Tipo *</label>
                                <input
                                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                                    placeholder="Ej: new_capture"
                                    value={templateForm.notification_type}
                                    onChange={e => setTemplateForm(p => ({ ...p, notification_type: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Título *</label>
                                <input
                                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                                    placeholder="Título de la notificación"
                                    value={templateForm.title}
                                    onChange={e => setTemplateForm(p => ({ ...p, title: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground">Mensaje *</label>
                                <textarea
                                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm resize-none"
                                    placeholder="Cuerpo del mensaje"
                                    rows={3}
                                    value={templateForm.message}
                                    onChange={e => setTemplateForm(p => ({ ...p, message: e.target.value }))}
                                />
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={templateForm.is_active}
                                    onChange={e => setTemplateForm(p => ({ ...p, is_active: e.target.checked }))}
                                    className="rounded border-border w-3.5 h-3.5 accent-primary"
                                />
                                <span className="text-xs text-foreground">Activa</span>
                            </label>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={() => setEditDialog({ open: false, template: null })}
                                className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => saveTemplateMutation.mutate({
                                    ...templateForm,
                                    id: editDialog.template?.id,
                                })}
                                disabled={!templateForm.category || !templateForm.title || !templateForm.message || saveTemplateMutation.isPending}
                                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                            >
                                {saveTemplateMutation.isPending ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
